<template>
    <div>
      <v-alert
        v-if="missingEndpoints.length > 0"
        type="warning"
        dense
        outlined
        class="mb-4"
      >
        {{ $t('permissoes.wizard.warnings.missingEndpoints', { keys: missingEndpoints.join(', ') }) }}
      </v-alert>
  
      <v-alert
        v-if="resources.length === 0"
        type="info"
        dense
        outlined
        class="mb-4"
      >
        {{ $t('permissoes.wizard.warnings.noResources') }}
      </v-alert>
  
      <v-expansion-panels v-model="openPanels" multiple accordion flat>
        <v-expansion-panel v-for="group in groupedResources" :key="group.type">
          <v-expansion-panel-header class="permissoes-panel-header py-2">
            <div class="permissoes-panel-header__title">
              <span class="font-weight-medium">{{ group.label }}</span>
              <v-chip
                x-small
                label
                color="primary"
                text-color="white"
                class="permissoes-panel-header__count ml-2"
              >
                {{ group.items.length }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="resource-chip-grid">
              <v-chip
                v-for="item in group.items"
                :key="item.id_recurso"
                small
                label
                outlined
                class="resource-chip"
                :close="canRemove(item)"
                close-icon="mdi-delete"
                @click:close="removeResource(item)"
              >
                <div class="resource-chip__content">
                  <span class="resource-chip__cod">{{ item.cod_recurso }}</span>
                  <span class="resource-chip__rota">{{ item.rota }}</span>
                </div>
              </v-chip>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
  
      <template v-if="allowResourceEdit">
        <v-divider class="my-4" />
        <div class="subtitle-2 mb-2">{{ $t('permissoes.wizard.review.addResources') }}</div>
        <v-row>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="addEndpointIds"
              :items="availableEndpoints"
              item-value="id_recurso"
              item-text="cod_recurso"
              :label="$t('permissoes.form.fields.endpoints')"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              dense
              outlined
              hide-details="auto"
              @change="onAddEndpoints"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="addTelaIds"
              :items="availableTelas"
              item-value="id_recurso"
              :item-text="telaItemText"
              :label="$t('permissoes.form.fields.screens')"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              dense
              outlined
              hide-details="auto"
              @change="onAddTelas"
            />
          </v-col>
        </v-row>
      </template>
    </div>
  </template>
  
  <script>
  const GROUP_ORDER = ["Tela", "Endpoint", "Empresa", "Unidade", "DashboardBI"];
  
  export default {
    name: "PermissaoResourceReview",
    props: {
      resources: {
        type: Array,
        default: () => [],
      },
      permissionType: {
        type: String,
        default: null,
      },
      missingEndpoints: {
        type: Array,
        default: () => [],
      },
      allRecursos: {
        type: Array,
        default: () => [],
      },
      allowResourceEdit: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        openPanels: [0, 1],
        addEndpointIds: [],
        addTelaIds: [],
      };
    },
    computed: {
      groupedResources() {
        const byType = {};
        for (const r of this.resources) {
          const type = r.descricao_tipo_recurso || "Outro";
          if (!byType[type]) byType[type] = [];
          byType[type].push(r);
        }
        return GROUP_ORDER.filter((t) => byType[t]?.length)
          .concat(Object.keys(byType).filter((t) => !GROUP_ORDER.includes(t)))
          .map((type) => ({
            type,
            label: this.typeLabel(type),
            items: byType[type],
          }));
      },
      selectedIds() {
        return new Set(this.resources.map((r) => r.id_recurso));
      },
      availableEndpoints() {
        return this.allRecursos.filter(
          (r) =>
            r.descricao_tipo_recurso === "Endpoint" &&
            !this.selectedIds.has(r.id_recurso)
        );
      },
      availableTelas() {
        return this.allRecursos.filter(
          (r) =>
            r.descricao_tipo_recurso === "Tela" &&
            !this.selectedIds.has(r.id_recurso)
        );
      },
    },
    methods: {
      typeLabel(type) {
        const key = {
          Tela: "permissoes.wizard.types.screen",
          Endpoint: "permissoes.wizard.types.integration",
          Empresa: "permissoes.wizard.types.company",
          Unidade: "permissoes.wizard.types.unit",
          DashboardBI: "permissoes.wizard.types.dashboard",
        }[type];
        return key ? this.$t(key) : type;
      },
      telaItemText(item) {
        return item.nome ? `${item.nome} (${item.cod_recurso})` : item.cod_recurso;
      },
      canRemove(item) {
        if (!this.allowResourceEdit) return false;
        const entityTypes = ["Empresa", "Unidade", "DashboardBI"];
        if (
          entityTypes.includes(item.descricao_tipo_recurso) &&
          ["company", "unit", "dashboard"].includes(this.permissionType)
        ) {
          return false;
        }
        return true;
      },
      removeResource(item) {
        this.$emit(
          "update:resources",
          this.resources.filter((r) => r.id_recurso !== item.id_recurso)
        );
      },
      onAddEndpoints(ids) {
        const toAdd = this.allRecursos.filter(
          (r) => ids.includes(r.id_recurso) && !this.selectedIds.has(r.id_recurso)
        );
        if (toAdd.length) {
          this.$emit("update:resources", [...this.resources, ...toAdd]);
        }
        this.addEndpointIds = [];
      },
      onAddTelas(ids) {
        const toAdd = this.allRecursos.filter(
          (r) => ids.includes(r.id_recurso) && !this.selectedIds.has(r.id_recurso)
        );
        if (toAdd.length) {
          this.$emit("update:resources", [...this.resources, ...toAdd]);
        }
        this.addTelaIds = [];
      },
    },
  };
  </script>
  
  <style scoped>
  .permissoes-panel-header >>> .v-expansion-panel-header__icon {
    margin-left: auto;
  }
  
  .permissoes-panel-header__title {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    max-width: calc(100% - 32px);
  }
  
  .permissoes-panel-header__count {
    flex-shrink: 0;
    min-width: 24px;
    justify-content: center;
    height: 20px !important;
    padding: 0 6px !important;
  }
  
  .resource-chip-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 8px;
    padding: 4px 0 8px;
  }
  
  @media (min-width: 1264px) {
    .resource-chip-grid {
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }
  }
  
  @media (min-width: 1904px) {
    .resource-chip-grid {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
  }
  
  .resource-chip {
    height: auto !important;
    min-height: 32px;
    min-width: 0;
    max-width: 100%;
    justify-content: flex-start;
    padding: 4px 10px !important;
  }
  
  .resource-chip >>> .v-chip__close {
    color: #f44336 !important;
  }
  
  .resource-chip >>> .v-chip__close .v-icon {
    color: #f44336 !important;
  }
  
  .resource-chip__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
    max-width: 100%;
    overflow: hidden;
  }
  
  .resource-chip__cod {
    font-weight: 500;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .resource-chip__rota {
    font-size: 10px;
    opacity: 0.75;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  </style>